import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ProgressBar } from "@/components/OwnToggle";
import { milestoneLabel } from "@/milestones";
import MediaImage from "@/components/MediaImage";

const mono = "font-mono2 tracking-[.04em]";
const DASH = "—";
const fmtDate = (v, lang) => (v ? new Date(v).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "short" }) : DASH);

function Section({ title, meta, testid, children }) {
  return (
    <section data-testid={testid} className="border border-[var(--d-line)] bg-[var(--d-panel)]">
      <header className="flex flex-wrap items-center gap-3 border-b border-[var(--d-line)] px-4 py-2.5">
        <h2 className="font-sg text-sm font-bold uppercase tracking-[-0.02em] text-[var(--d-text)]">{title}</h2>
        {meta}
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

function SortChips({ options, value, onChange, prefix }) {
  return (
    <div className="ml-auto flex gap-1" data-testid={`${prefix}-sort`}>
      {options.map(([k, label]) => (
        <button key={k} type="button" onClick={() => onChange(k)} className={`${mono} border px-2 py-1 text-[9px] uppercase ${value === k ? "border-[var(--d-accent)] text-[var(--d-accent)]" : "border-[var(--d-line)] text-[var(--d-muted)] hover:text-[var(--d-text)]"}`} data-testid={`${prefix}-sort-${k}`}>{label}</button>
      ))}
    </div>
  );
}

export function CollectionWeek({ rows, t, lang }) {
  return (
    <div className="mb-8 border border-[var(--d-accent)]/50 bg-[var(--d-accent)]/5 px-4 py-3" data-testid="collection-week">
      <p className="font-sg text-sm font-bold uppercase tracking-[-0.02em] text-[var(--d-text)]" data-testid="collection-week-title">
        {rows.length ? t("coll.week_title", { n: rows.length }) : t("coll.week_none")}
      </p>
      {rows.length > 0 && (
        <ul className="mt-2 divide-y divide-[var(--d-line)]">
          {rows.map((r) => (
            <li key={`${r.provider}:${r.player_id}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1.5" data-testid={`collection-week-row-${r.player_id}`}>
              <Link to={`/players/${r.player_id}`} className={`${mono} text-xs font-bold text-[var(--d-text)] hover:text-[var(--d-accent)]`}>{r.name}</Link>
              <span className={`${mono} text-[10px] text-[var(--d-muted)]`}>{r.club || DASH}</span>
              <span className={`${mono} text-[10px] text-[var(--d-accent)]`}>{milestoneLabel(r.last_milestone, t)} · {fmtDate(r.last_milestone.date, lang)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SetRow({ s, t }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="py-2.5" data-testid={`collection-set-${s.slug}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Link to={`/cards/${s.slug}`} className={`${mono} min-w-[60%] flex-1 text-xs font-bold text-[var(--d-text)] hover:text-[var(--d-accent)] sm:min-w-0 sm:truncate`}>{s.set_name}</Link>
        <span className={`${mono} text-[10px] text-[var(--d-muted)]`}>{s.manufacturer} · {s.season_short}</span>
        <span className={`${mono} text-xs font-bold tabular-nums text-[var(--d-accent)]`} data-testid={`collection-set-count-${s.slug}`}>{s.owned} / {s.total} · {s.pct} %</span>
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className={`${mono} border border-[var(--d-line)] px-2 py-1 text-[9px] uppercase text-[var(--d-muted)] hover:text-[var(--d-text)]`} data-testid={`collection-set-toggle-${s.slug}`}>{open ? "▾" : "▸"} {t("coll.subsets_toggle")}</button>
      </div>
      <div className="mt-1.5"><ProgressBar owned={s.owned} total={s.total} pct={s.pct} testid={`collection-set-bar-${s.slug}`} /></div>
      {open && (
        <ul className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2" data-testid={`collection-set-subsets-${s.slug}`}>
          {s.subsets.map((g) => (
            <li key={g.name} className="flex items-center gap-2">
              <span className={`${mono} w-36 truncate text-[10px] text-[var(--d-muted)]`}>{g.name}</span>
              <span className="flex-1"><ProgressBar owned={g.owned} total={g.total} pct={g.pct} compact /></span>
              <span className={`${mono} w-16 text-right text-[10px] tabular-nums text-[var(--d-text)]`}>{g.owned}/{g.total}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function CollectionSets({ sets, t }) {
  const [sort, setSort] = useState("progress");
  const sorted = useMemo(() => [...sets].sort((a, b) =>
    sort === "name" ? String(a.set_name).localeCompare(String(b.set_name))
      : sort === "added" ? String(b.last_added).localeCompare(String(a.last_added))
        : b.pct - a.pct || b.owned - a.owned), [sets, sort]);
  return (
    <Section title={t("coll.sets_title")} testid="collection-sets"
      meta={<SortChips prefix="collection-sets" value={sort} onChange={setSort} options={[["progress", t("coll.sort_progress")], ["name", t("coll.sort_name")], ["added", t("coll.sort_added")]]} />}>
      <ul className="divide-y divide-[var(--d-line)]">{sorted.map((s) => <SetRow key={s.set_id} s={s} t={t} />)}</ul>
    </Section>
  );
}

export function CollectionPlayers({ rows, t }) {
  const [sort, setSort] = useState("hype");
  const sorted = useMemo(() => [...rows].sort((a, b) =>
    sort === "activity" ? String(b.last_milestone?.date || "").localeCompare(String(a.last_milestone?.date || ""))
      : sort === "cards" ? b.cards_owned - a.cards_owned
        : (b.hype ?? -1) - (a.hype ?? -1)), [rows, sort]);
  return (
    <Section title={t("coll.players_title")} testid="collection-players"
      meta={<SortChips prefix="collection-players" value={sort} onChange={setSort} options={[["hype", t("coll.sort_hype")], ["activity", t("coll.sort_activity")], ["cards", t("coll.sort_cards")]]} />}>
      <table className={`${mono} w-full text-[11px]`}>
        <thead className="text-[9px] uppercase text-[var(--d-muted)]">
          <tr className="border-b border-[var(--d-line)] text-left"><th className="py-1 pr-2">{t("sets.player")}</th><th className="hidden pr-2 sm:table-cell">{t("coll.club")}</th><th className="pr-2 text-right">Hype</th><th className="hidden pr-2 md:table-cell">{t("coll.last_milestone")}</th><th className="pr-2 text-right">{t("coll.sort_cards")}</th></tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={`${r.provider}:${r.player_id}`} className="border-b border-[var(--d-line)]/60" data-testid={`collection-player-${r.player_id}`}>
              <td className="py-1.5 pr-2">
                <Link to={`/players/${r.player_id}`} className="inline-flex items-center gap-2 font-bold text-[var(--d-text)] hover:text-[var(--d-accent)]">
                  <MediaImage src={r.club_logo} alt={t("media.logo_alt", { club: r.club || "—" })} kind="club" size={24} radius="rounded" surface="my_collection" imgClassName="object-contain p-0.5" testid={`collection-player-media-${r.player_id}`} />
                  {r.name}
                </Link>
              </td>
              <td className="hidden pr-2 text-[var(--d-muted)] sm:table-cell">{r.club || DASH}</td>
              <td className="pr-2 text-right font-bold text-[var(--d-accent)]">{r.hype ?? DASH}</td>
              <td className="hidden pr-2 text-[var(--d-muted)] md:table-cell">{r.last_milestone ? milestoneLabel(r.last_milestone, t) : t("coll.no_milestone")}</td>
              <td className="pr-2 text-right tabular-nums text-[var(--d-text)]">{r.cards_owned}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

export function CollectionMissing({ rows, markets, t }) {
  return (
    <Section title={t("coll.missing_title")} testid="collection-missing"
      meta={<span className={`${mono} text-[9px] uppercase text-[var(--d-muted)]`}>{t("coll.missing_hint")}</span>}>
      {rows.length === 0 ? <p className={`${mono} text-[11px] text-[var(--d-muted)]`} data-testid="collection-missing-none">{t("coll.missing_none")}</p> : (
        <ul className="divide-y divide-[var(--d-line)]">
          {rows.map((r) => (
            <li key={r.card_id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-1.5" data-testid={`collection-missing-${r.card_id}`}>
              <Link to={`/players/${r.player_id}`} className={`${mono} text-xs font-bold text-[var(--d-text)] hover:text-[var(--d-accent)]`}>{r.name}</Link>
              <span className={`${mono} text-[10px] font-bold text-[var(--d-accent)]`}>{r.hype ?? DASH}</span>
              <Link to={`/cards/${r.card_id}`} className={`${mono} text-[10px] text-[var(--d-muted)] hover:text-[var(--d-text)]`}>#{r.card_number || DASH} · {r.subset} · {r.set_name}</Link>
              {markets[0] && <a href={markets[0].url.replace("{QUERY}", r.market_query)} target="_blank" rel="noopener noreferrer nofollow" className={`${mono} ml-auto text-[10px] uppercase text-[var(--d-muted)] hover:text-[var(--d-accent)]`} data-testid={`collection-missing-market-${r.card_id}`}>{t("coll.check_listings")} ↗</a>}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
